import { stripHtmlToPlainText } from '@/lib/landing-html'

type LandingMetaImage = {
  image_url?: string | null
}

type LandingMetaCampaign = {
  name?: string | null
  title?: string | null
  description?: string | null
  prize_description?: string | null
  public_notice_text?: string | null
  public_page_title?: string | null
  public_share_title?: string | null
  public_share_description?: string | null
  public_share_image_url?: string | null
  main_image_url?: string | null
  campaign_images?: LandingMetaImage[] | null
}

export type LandingResolvedMeta = {
  pageTitle: string
  shareTitle: string
  shareDescription: string
  shareImageUrl: string | null
}

const DEFAULT_SHARE_DESCRIPTION =
  'Participe da campanha, veja os detalhes e acompanhe todas as informações desta página.'

function normalizeText(value: string | null | undefined): string {
  return stripHtmlToPlainText(value)
}

function pickFirstText(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const normalized = normalizeText(value)
    if (normalized) return normalized
  }

  return ''
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
}

export function resolveLandingMeta(campaign: LandingMetaCampaign | null | undefined): LandingResolvedMeta {
  const baseTitle = pickFirstText(campaign?.title, campaign?.name, 'Campanha')
  const pageTitleBase = pickFirstText(campaign?.public_page_title, campaign?.public_share_title, baseTitle)
  const shareTitle = pickFirstText(campaign?.public_share_title, campaign?.public_page_title, baseTitle)
  const shareDescription = truncate(
    pickFirstText(
      campaign?.public_share_description,
      campaign?.description,
      campaign?.prize_description,
      campaign?.public_notice_text,
      DEFAULT_SHARE_DESCRIPTION
    ),
    320
  )
  const shareImageUrl = pickFirstText(
    campaign?.public_share_image_url,
    campaign?.main_image_url,
    campaign?.campaign_images?.[0]?.image_url
  ) || null

  return {
    pageTitle: truncate(pageTitleBase, 255),
    shareTitle: truncate(shareTitle, 255),
    shareDescription,
    shareImageUrl,
  }
}

function upsertMeta(selector: string, attributes: Record<string, string>): void {
  if (typeof document === 'undefined') return

  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value)
  })
}

export function applyLandingDocumentMeta(campaign: LandingMetaCampaign | null | undefined): void {
  if (typeof document === 'undefined') return

  const meta = resolveLandingMeta(campaign)
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  document.title = meta.pageTitle

  upsertMeta('meta[name="description"]', {
    name: 'description',
    content: meta.shareDescription,
  })
  upsertMeta('meta[property="og:type"]', {
    property: 'og:type',
    content: 'website',
  })
  upsertMeta('meta[property="og:site_name"]', {
    property: 'og:site_name',
    content: 'Sortee',
  })
  upsertMeta('meta[property="og:title"]', {
    property: 'og:title',
    content: meta.shareTitle,
  })
  upsertMeta('meta[property="og:description"]', {
    property: 'og:description',
    content: meta.shareDescription,
  })
  upsertMeta('meta[property="og:url"]', {
    property: 'og:url',
    content: currentUrl,
  })
  upsertMeta('meta[name="twitter:card"]', {
    name: 'twitter:card',
    content: meta.shareImageUrl ? 'summary_large_image' : 'summary',
  })
  upsertMeta('meta[name="twitter:title"]', {
    name: 'twitter:title',
    content: meta.shareTitle,
  })
  upsertMeta('meta[name="twitter:description"]', {
    name: 'twitter:description',
    content: meta.shareDescription,
  })

  if (meta.shareImageUrl) {
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: meta.shareImageUrl,
    })
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: 'og:image:secure_url',
      content: meta.shareImageUrl,
    })
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: meta.shareImageUrl,
    })
  }
}
